import React, { useEffect, useRef, useState } from 'react';
import Axios from 'axios';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

/* ═══════════════════════════════════════════════════
   AutoCompLoc - pure Tailwind. Segmented "Airbnb-style".
   API contract preserved: { loc, setloc, error, label, required }
   ═══════════════════════════════════════════════════ */

export const AutoCompLoc = ({ loc, setloc, error, label = 'Location', required = true, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState(loc || '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setQuery(loc || ''); }, [loc]);

  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const fetchSuggestions = (value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value) { setOptions([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await Axios.get('/new_apis/get-location', {
          params: { search: value },
          headers: { key },
        });
        const places = res.data.preds || {};
        setOptions(Object.keys(places).map((k) => places[k].description));
      } catch {
        setOptions([]);
      }
      setLoading(false);
    }, 220);
  };

  const onChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setActive(0);
    setOpen(true);
    fetchSuggestions(v);
  };

  const onFocus = () => {
    setFocused(true);
    setOpen(true);
    if (query && options.length === 0) fetchSuggestions(query);
  };

  const onBlur = () => setFocused(false);

  const select = (val) => {
    setloc(val);
    setQuery(val);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && open && options[active]) { e.preventDefault(); select(options[active]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  const showDropdown = open && (loading || options.length > 0 || query.length > 0);

  /* ── compact mode (44px form field) ── */
  if (compact) {
    const compactCls = `
      relative flex h-11 w-full items-center gap-2 rounded-lg px-3 cursor-text
      bg-surface-overlay/70
      border
      transition-[border-color,background-color,box-shadow] duration-200 ease-out-soft
      ${error
        ? 'border-red-400/55 focus-within:border-red-400'
        : focused
          ? 'border-amber bg-surface-overlay shadow-[0_0_0_3px_rgba(251,191,36,0.22)]'
          : 'border-amber/22 hover:border-amber/55'}
    `;
    return (
      <div ref={wrapRef} className="relative w-full">
        <label
          className={compactCls}
          onClick={() => inputRef.current && inputRef.current.focus()}
        >
          <span className={`shrink-0 ${focused ? 'text-amber' : 'text-amber/70'} transition-colors duration-200`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
              <path d="M12 22s-8-7.5-8-13a8 8 0 1 1 16 0c0 5.5-8 13-8 13z" />
              <circle cx="12" cy="9" r="3" />
            </svg>
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            placeholder="Type a city or area"
            autoComplete="off"
            className="w-full bg-transparent text-[14px] font-medium text-fg-primary leading-none placeholder:text-fg-muted outline-none border-0 p-0"
          />
          {loading && (
            <span className="shrink-0 text-amber/80">
              <svg viewBox="0 0 24 24" className="h-4 w-4 animate-spin" aria-hidden>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" fill="none" strokeDasharray="40 60" strokeLinecap="round" />
              </svg>
            </span>
          )}
        </label>
        {showDropdown && (
          <div
            role="listbox"
            className="
              absolute left-0 right-0 top-full z-50 mt-2
              max-h-72 overflow-auto rounded-xl
              border border-amber/30 bg-surface-elevated
              shadow-dialog
              animate-[fadeIn_140ms_ease-out]
            "
          >
            {loading && options.length === 0 && (
              <div className="px-3.5 py-2.5 text-[13px] text-fg-tertiary">Fetching location…</div>
            )}
            {!loading && options.length === 0 && query.length > 0 && (
              <div className="px-3.5 py-2.5 text-[13px] text-fg-tertiary">No locations found.</div>
            )}
            {options.map((opt, i) => (
              <button
                type="button"
                key={`${opt}-${i}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => select(opt)}
                className={`
                  flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13.5px]
                  transition-colors duration-150 ease-out-soft
                  ${i === active
                    ? 'bg-amber/14 text-amber'
                    : 'text-fg-secondary hover:bg-amber/8 hover:text-fg-primary'}
                `}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 shrink-0 opacity-80">
                  <path d="M12 22s-8-7.5-8-13a8 8 0 1 1 16 0c0 5.5-8 13-8 13z" />
                  <circle cx="12" cy="9" r="3" />
                </svg>
                <span className="truncate">{opt}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ── original 72px segmented mode (hero search) ── */
  const insetSegment = `
    group relative flex h-[72px] w-full items-center gap-3 rounded-xl px-4
    transition-[background-color] duration-200 ease-out-soft
    hover:bg-amber/5
  `;
  const insetActive = 'bg-amber/8';
  const insetError = 'bg-red-400/10';
  const cls = `${insetSegment} ${error ? insetError : (focused ? insetActive : '')} cursor-text`;

  return (
    <div ref={wrapRef} className="relative w-full">
      <label
        className={cls}
        onClick={() => inputRef.current && inputRef.current.focus()}
      >
        {/* leading icon */}
        <span className={`shrink-0 ${focused ? 'text-amber' : 'text-fg-tertiary'} transition-colors duration-200 ease-out-soft`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
            <path d="M12 22s-8-7.5-8-13a8 8 0 1 1 16 0c0 5.5-8 13-8 13z" />
            <circle cx="12" cy="9" r="3" />
          </svg>
        </span>

        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-fg-tertiary leading-none mb-1.5">
            {label}
            {required && <span className="ml-1 text-amber">*</span>}
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            placeholder="Where are you looking?"
            autoComplete="off"
            className="w-full bg-transparent text-[14.5px] font-medium text-fg-primary leading-none placeholder:text-fg-tertiary outline-none border-0 p-0"
          />
        </span>

        {loading && (
          <span className="shrink-0 text-fg-tertiary">
            <svg viewBox="0 0 24 24" className="h-4 w-4 animate-spin" aria-hidden>
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" fill="none" strokeDasharray="40 60" strokeLinecap="round" />
            </svg>
          </span>
        )}
      </label>

      {showDropdown && (
        <div
          role="listbox"
          className="
            absolute left-0 right-0 top-full z-50 mt-2
            max-h-80 overflow-auto rounded-xl
            border border-border-subtle bg-surface-elevated
            shadow-dialog
            animate-[fadeIn_140ms_ease-out]
          "
        >
          {loading && options.length === 0 && (
            <div className="px-3.5 py-2.5 text-[13px] text-fg-tertiary">Fetching location…</div>
          )}
          {!loading && options.length === 0 && query.length > 0 && (
            <div className="px-3.5 py-2.5 text-[13px] text-fg-tertiary">No locations found.</div>
          )}
          {options.map((opt, i) => (
            <button
              type="button"
              key={`${opt}-${i}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => select(opt)}
              className={`
                flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13.5px]
                transition-colors duration-150 ease-out-soft
                ${i === active
                  ? 'bg-amber/12 text-amber'
                  : 'text-fg-secondary hover:bg-amber/8 hover:text-fg-primary'
                }
              `}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 shrink-0 opacity-70">
                <path d="M12 22s-8-7.5-8-13a8 8 0 1 1 16 0c0 5.5-8 13-8 13z" />
                <circle cx="12" cy="9" r="3" />
              </svg>
              <span className="truncate">{opt}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
